import React, { useMemo, useState } from "react";
import { useCommunicationsFeedbacks } from "./hooks/feedbacks.hooks";
import CommunicationFeedbacksTable from "./components/DataTable/CommunicationFeedbacksTable";
import {
  CommunicationFeedback,
  CommunicationFeedbackFilter,
} from "@/services/interfaces/IFeedbackService";
import CommunicationFeedbacksFilters from "./components/filters/communication-feedbacks.filter";
import { CommunicationFeedbackModal } from "./components/CommunicationFeedbackModal";

const PER_PAGE = 10;
type SortBy = NonNullable<CommunicationFeedbackFilter["sortBy"]>;

export const CommunicationFeedbacks: React.FC<{ hotelId: string }> = ({
  hotelId,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<
    Omit<CommunicationFeedbackFilter, "sortBy" | "page" | "limit" | "hotelId">
  >({
    roomIds: [],
    consiergeIds: [],
    minRating: 1,
  });
  const [sortBy, setSortBy] = useState<SortBy | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | null>(null);

  const [selectedFeedback, setSelectedFeedback] =
    useState<CommunicationFeedback | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const feedbacksFilters = useMemo(() => {
    return {
      ...filters,
      page: currentPage,
      limit: PER_PAGE,
      sortBy: sortBy ?? undefined,
      sortOrder: sortOrder ?? undefined,
      hotelId,
    };
  }, [filters, currentPage, sortBy, sortOrder, hotelId]);

  const { data, isLoading } = useCommunicationsFeedbacks(feedbacksFilters);
  const feedbacks = data?.data ?? [];
  const totalPages = data?.total
    ? Math.max(1, Math.ceil(data?.total / data.limit))
    : 1;

  const handleSort = (field: string) => {
    const newField = field as SortBy;
    if (sortBy !== newField) {
      setSortBy(newField);
      setSortOrder("asc");
    } else {
      if (sortOrder === "asc") setSortOrder("desc");
      else if (sortOrder === "desc") {
        setSortBy(null);
        setSortOrder(null);
      } else setSortOrder("asc");
    }
  };

  const handleRowClick = (feedback: CommunicationFeedback) => {
    setSelectedFeedback(feedback);
    setIsModalOpen(true);
  };

  return (
    <div className="flex flex-col p-4 gap-4">
      <CommunicationFeedbacksFilters filters={filters} onChange={setFilters} />

      <CommunicationFeedbacksTable
        data={feedbacks}
        isLoading={isLoading}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        currentPage={currentPage}
        totalPages={totalPages || 1}
        onPageChange={setCurrentPage}
        onRowClick={handleRowClick}
      />

      <CommunicationFeedbackModal
        feedback={selectedFeedback}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};
